import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import BusinessLoader from "../../../common/BusinessLoader";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { handleApi } from "../../../../apis";
import { GETWALLETBILLPAYMENT } from "../../../../apis/configuration";
import { downloadReceiptImage, printWalletReceiptPreview ,copyReceiptImage} from "../../../../utils";
import UCashTransactionSuccess from "../../../common/UCashTransactionSuccess";
import UCashTransactionReceipt from "../../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../../common/DownloadReceipt";
import { formatNum } from "../../../../oscar-pos-core/constants";

const BillTransactionSuccess = () => {
  const [I18n] = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [transactionDetail, setTransactionDetail] = useState({});
  const [state, setState] = useState({
    amount: 0,
    logo: "",
    date: null,
    commission: 0,
  });

  const receiptRef = useRef();
  const timeoutId = useRef(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { business } = useAuthContext();

  useEffect(() => {
    fetchWalletBillTransaction();
    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId.current);
    };
  }, []);

  const fetchWalletBillTransaction = async () => {
    if (location?.state?.data && location?.state?.transaction) {
      setState({ ...location.state.data });
      setTransactionDetail({ ...location.state.transaction });
      return;
    }
    setIsLoading(true);
    try {
      const response = await handleApi(
        GETWALLETBILLPAYMENT,
        { billing_key: params?.id },
        null,
        navigate,
      );
      if (!isMounted.current) return;
      if (response?.status === "COMPLETED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        setIsLoading(false);
        const transaction = response || {};
        const transaction_details = transaction?.transaction_details || {};
        setTransactionDetail(transaction_details);
        setState({
          amount: Math.abs(transaction?.amount),
          logo: transaction?.icon || "",
          date: transaction_details?.created_at,
          commission: transaction?.commission || 0,
        });
      } else if (response?.status === "FAILED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.bill_fetching_failed,
          severity: "error",
          icon: "",
        });
        navigate("/UCash");
      } else {
        setIsLoading(true);
        timeoutId.current = setTimeout(fetchWalletBillTransaction, 5000);
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (timeoutId.current) clearTimeout(timeoutId.current);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_fetching_bill_transaction,
        severity: "error",
        icon: "",
      });
      setIsLoading(false);
      navigate("/UCash");
    }
  };

  const handleDownload = () =>
    downloadReceiptImage(receiptRef, {
      setIsDownloadLoading,
      analyticsEvent: "w_earn_bill_download",
      fileName: "Rupin Bill Payment Receipt",
    });

  const handleCopy = () =>
    copyReceiptImage(receiptRef, { showSnackbar, I18n, analyticsEvent: "w_earn_bill_copy_receipt" });

  const tid = transactionDetail?.id || params?.id || "";
  const consumerNumber = transactionDetail?.consumer_number || "";
  const consumerName = transactionDetail?.consumer_name || "";
  const customerName = transactionDetail?.customer_name || "";
  const billCompany = transactionDetail?.bill_company || "";
  const billLogo = transactionDetail?.logo_link || state.logo || "";
  const formattedDate = state.date ? moment(state.date).format("D MMMM YYYY, hh:mm A") : "";

  // Whole phrase from one key so each language sets its own order ("{name} کو بل کی ادائیگی" in
  // Urdu). Direction comes from the template, not dir="auto": the Urdu line starts with the
  // (Latin) name, which would wrongly make it left-to-right.
  const billPaymentTo = I18n.bill_payment_to_name || "Bill Payment to {name}";
  const [beforeName, afterName = ""] = billPaymentTo.split("{name}");
  const subtitleDir = /[\u0600-\u06FF]/.test(billPaymentTo) ? "rtl" : "ltr";
  const successSubtitle = (
    <span
      dir={subtitleDir}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 4 }}
    >
      <span>
        {consumerName ? (
          <>
            {beforeName}
            <strong>{consumerName}</strong>
            {afterName}
          </>
        ) : (
          I18n.bill_payment || "Bill payment"
        )}
      </span>
      {billLogo ? (
        <span style={{
          display: "inline-flex", width: 28, height: 28,
          borderRadius: 6, flexShrink: 0, overflow: "hidden",
          alignItems: "center", justifyContent: "center",
        }}>
          <img src={billLogo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </span>
      ) : null}
      {consumerNumber ? <strong>{consumerNumber}</strong> : null}
    </span>
  );

  const billBase = billCompany
    ? `${billCompany}${consumerNumber ? ` - ${consumerNumber}` : ""}`
    : consumerNumber;
  const receiptRecipient = consumerName ? (
    <>{billBase}<span className="newLightColor"> ({consumerName})</span></>
  ) : billBase;
  const receiptRecipientPlain = `${billBase}${consumerName ? ` (${consumerName})` : ""}`;

  const receiptRows = [
    { label: I18n.by || "By", value: business?.name || "-" },
    { label: I18n.tid, value: tid },
    { label: I18n.date || "Date", value: formattedDate },
    { label: I18n.fee || "Fee", value: transactionDetail?.fee ? `Rs. ${transactionDetail.fee}` : `Rs. 0 ${I18n.fee_free}` },
    { label: I18n.total_amount || "Total amount", value: `Rs. ${formatNum(state.amount) || 0}` },
  ];

  const handlePrint = () =>
    printWalletReceiptPreview(
      business,
      {
        headerText: `${I18n.bill_paid_with} <strong>${I18n.rupin}</strong>`,
        amount: state.amount,
        customerName,
        recipientName: receiptRecipientPlain,
        bankInfo: "",
        rows: receiptRows,
      },
      { showSnackbar, setIsPrintLoading, analyticsEvent: "w_earn_bill_print" },
    );

  if (isLoading) return <BusinessLoader loadBusiness />;

  return (
    <Fragment>
      {showReceipt ? (
        <UCashTransactionReceipt
          amount={state.amount}
          headerText={
            <>
              {I18n.bill_paid_with} <strong>{I18n.rupin}</strong>
            </>
          }
          recipientName={receiptRecipient}
          customerName={customerName}
          bankInfo=""
          rows={receiptRows}
          onBack={() => setShowReceipt(false)}
          onPrint={handlePrint}
          isPrintLoading={isPrintLoading}
          onCopy={handleCopy}

        />
      ) : (
        <UCashTransactionSuccess
          subtitle={successSubtitle}
          tid={tid}
          amount={state.amount}
          processingTime={"2.6 sec"}
          onViewReceipt={() => setShowReceipt(true)}
          onClose={() => {
            triggerLogEvent(analytics, "w_earn_bill_back");
            navigate("/UCash/BillTransactions");
          }}
          onDownload={handleDownload}
          isDownloadLoading={isDownloadLoading}
          onBack={() => navigate("/UCash/BillTransactions")}
        />
      )}

      <DownloadReceipt
        ref={receiptRef}
        amount={state.amount}
        headerText={I18n.bill_paid_with_rupin}
        customerName={customerName}
        recipientName={receiptRecipientPlain}
        bankInfo=""
        rows={receiptRows}
      />
    </Fragment>
  );
};

export default BillTransactionSuccess;
